/**
 * Batch Import Service for CSB Library Bulk Ingestion
 *
 * Supports Path B (PPI-managed) batch processing of CSB reports.
 * Each report is tracked in Hugo_Documents with a batchId for grouping.
 * Webhook calls are rate-limited to avoid overwhelming n8n/AI processing.
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { collections } from '../utils/firestore';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default n8n webhook URL for Path B (PPI-managed) ingestion */
const N8N_WEBHOOK_URL =
  import.meta.env.VITE_N8N_WEBHOOK_URL || 'https://n8n.ppiweb.com/webhook/csb-ingest';

/** Delay between webhook calls to avoid overwhelming processing (ms) */
const RATE_LIMIT_DELAY_MS = 3000; // 3 seconds between calls

/** Known CSB report templates for quick import */
export const CSB_REPORT_LIBRARY = [
  {
    id: 'chevron-richmond-2012',
    title: 'Chevron Richmond Refinery Fire (2012)',
    url: 'https://www.csb.gov/chevron-richmond-refinery-fire/',
    pdfUrl: 'https://www.csb.gov/assets/1/20/chevron_final_investigation_report_2015-01-28.pdf',
    incidentDate: '2012-08-06',
    industry: 'oil_gas',
    description: 'Catastrophic pipe failure released flammable hydrocarbon, creating a large vapor cloud fire. 15,000+ community members sought medical attention.',
  },
  {
    id: 'bp-texas-city-2005',
    title: 'BP Texas City Refinery Explosion (2005)',
    url: 'https://www.csb.gov/bp-america-refinery-explosion/',
    pdfUrl: 'https://www.csb.gov/assets/1/20/csbfinalreportbp.pdf',
    incidentDate: '2005-03-23',
    industry: 'oil_gas',
    description: 'Explosion during startup of an isomerization unit killed 15 workers and injured 180. Investigated organizational and safety culture failures.',
  },
  {
    id: 'deepwater-horizon-2010',
    title: 'Deepwater Horizon Blowout (2010)',
    url: 'https://www.csb.gov/macondo-blowout-and-explosion/',
    pdfUrl: 'https://www.csb.gov/assets/1/20/overview_-_final.pdf',
    incidentDate: '2010-04-20',
    industry: 'oil_gas',
    description: 'Blowout, explosion and fire on the Deepwater Horizon drilling rig killed 11 workers and caused the largest marine oil spill in U.S. history.',
  },
  {
    id: 'tesoro-anacortes-2010',
    title: 'Tesoro Anacortes Refinery Explosion (2010)',
    url: 'https://www.csb.gov/tesoro-refinery-fatal-explosion-and-fire/',
    pdfUrl: 'https://www.csb.gov/assets/1/20/tesoro_anacortes_2014-may-01.pdf',
    incidentDate: '2010-04-02',
    industry: 'oil_gas',
    description: 'Heat exchanger rupture during startup killed 7 workers. Investigation revealed inadequate equipment inspection and maintenance practices.',
  },
  {
    id: 'imperial-sugar-2008',
    title: 'Imperial Sugar Dust Explosion (2008)',
    url: 'https://www.csb.gov/imperial-sugar-company-dust-explosion-and-fire/',
    pdfUrl: 'https://www.csb.gov/assets/1/20/imperial_sugar_report_final_updated.pdf',
    incidentDate: '2008-02-07',
    industry: 'manufacturing',
    description: 'Sugar dust explosion at Imperial Sugar refinery killed 14 workers. Highlighted combustible dust hazards and inadequate housekeeping.',
  },
];

// ---------------------------------------------------------------------------
// Batch Management
// ---------------------------------------------------------------------------

/**
 * Create a new import batch and queue reports for processing.
 *
 * @param {Object} params
 * @param {Array<{title: string, url: string, pdfUrl?: string, incidentDate?: string, industry?: string, description?: string}>} params.reports - Reports to import
 * @param {string} params.createdBy - UID of the user creating the batch
 * @param {string} [params.batchName] - Optional human-readable batch name
 * @returns {Promise<{batchId: string, documentIds: string[]}>}
 */
export async function createBatch({ reports, createdBy, batchName }) {
  if (!reports || reports.length === 0) {
    throw new Error('At least one report is required');
  }

  const now = Timestamp.now();
  const batchId = `batch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const documentIds = [];

  for (const report of reports) {
    const docData = {
      organizationId: null, // PPI-managed, global
      batchId,
      batchName: batchName || `CSB Import ${new Date().toLocaleDateString()}`,
      fileName: report.pdfUrl ? report.pdfUrl.split('/').pop() : `${report.title}.pdf`,
      title: report.title,
      description: report.description || '',
      sourceUrl: report.url || '',
      pdfUrl: report.pdfUrl || '',
      incidentDate: report.incidentDate || null,
      industry: report.industry || '',
      source: 'csb',
      ppiManaged: true,
      targetVisibility: 'global',
      status: 'queued', // queued → processing → completed | error
      processingStartedAt: null,
      processingCompletedAt: null,
      error: null,
      webhookResponseId: null,
      createdBy,
      createdAt: now,
      updatedAt: now,
    };

    const colRef = collection(db, collections.documents);
    const docRef = await addDoc(colRef, docData);
    documentIds.push(docRef.id);
  }

  return { batchId, documentIds };
}

/**
 * Get all documents in a specific batch.
 *
 * @param {string} batchId
 * @returns {Promise<Array<{id: string, ...data}>>}
 */
export async function getBatchDocuments(batchId) {
  const colRef = collection(db, collections.documents);
  const q = query(
    colRef,
    where('batchId', '==', batchId),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to real-time updates on batch documents.
 *
 * @param {string} batchId
 * @param {Function} callback - Called with array of {id, ...data}
 * @returns {Function} Unsubscribe function
 */
export function subscribeToBatch(batchId, callback) {
  const colRef = collection(db, collections.documents);
  const q = query(
    colRef,
    where('batchId', '==', batchId),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(q, (snapshot) => {
    const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(docs);
  }, (error) => {
    console.error('Batch subscription error:', error);
    callback([]);
  });
}

/**
 * Get all batches (unique batchIds) with summary stats.
 *
 * @returns {Promise<Array<{batchId: string, batchName: string, total: number, queued: number, processing: number, completed: number, errored: number, createdAt: any}>>}
 */
export async function getAllBatches() {
  const colRef = collection(db, collections.documents);
  const q = query(
    colRef,
    where('batchId', '!=', null),
    orderBy('batchId', 'asc'),
    orderBy('createdAt', 'desc')
  );

  let snapshot;
  try {
    snapshot = await getDocs(q);
  } catch (error) {
    // Fallback: if index not available, fetch all ppiManaged docs
    console.warn('Batch query failed, using fallback:', error.message);
    const fallbackQ = query(
      colRef,
      where('ppiManaged', '==', true),
      orderBy('createdAt', 'desc')
    );
    snapshot = await getDocs(fallbackQ);
  }

  const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Group by batchId
  const batchMap = new Map();
  for (const d of docs) {
    if (!d.batchId) continue;
    if (!batchMap.has(d.batchId)) {
      batchMap.set(d.batchId, {
        batchId: d.batchId,
        batchName: d.batchName || d.batchId,
        total: 0,
        queued: 0,
        processing: 0,
        completed: 0,
        errored: 0,
        createdAt: d.createdAt,
        createdBy: d.createdBy,
      });
    }
    const batch = batchMap.get(d.batchId);
    batch.total++;
    if (d.status === 'queued') batch.queued++;
    else if (d.status === 'processing') batch.processing++;
    else if (d.status === 'completed') batch.completed++;
    else if (d.status === 'error') batch.errored++;
  }

  // Sort by createdAt descending
  return Array.from(batchMap.values()).sort((a, b) => {
    const aTime = a.createdAt?.seconds || 0;
    const bTime = b.createdAt?.seconds || 0;
    return bTime - aTime;
  });
}

/**
 * Subscribe to real-time updates on all batches.
 *
 * @param {Function} callback - Called with array of batch summary objects
 * @returns {Function} Unsubscribe function
 */
export function subscribeToAllBatches(callback) {
  const colRef = collection(db, collections.documents);
  const q = query(
    colRef,
    where('ppiManaged', '==', true),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

    const batchMap = new Map();
    for (const d of docs) {
      if (!d.batchId) continue;
      if (!batchMap.has(d.batchId)) {
        batchMap.set(d.batchId, {
          batchId: d.batchId,
          batchName: d.batchName || d.batchId,
          total: 0,
          queued: 0,
          processing: 0,
          completed: 0,
          errored: 0,
          createdAt: d.createdAt,
          createdBy: d.createdBy,
        });
      }
      const batch = batchMap.get(d.batchId);
      batch.total++;
      if (d.status === 'queued') batch.queued++;
      else if (d.status === 'processing') batch.processing++;
      else if (d.status === 'completed') batch.completed++;
      else if (d.status === 'error') batch.errored++;
    }

    const sorted = Array.from(batchMap.values()).sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime;
    });

    callback(sorted);
  }, (error) => {
    console.error('All batches subscription error:', error);
    callback([]);
  });
}

// ---------------------------------------------------------------------------
// Webhook Processing with Rate Limiting
// ---------------------------------------------------------------------------

/**
 * Helper to delay execution.
 * @param {number} ms
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fire the n8n webhook for a single document.
 *
 * @param {Object} docData - The document data
 * @param {string} docId - Firestore document ID
 * @returns {Promise<{success: boolean, responseId?: string, error?: string}>}
 */
async function fireWebhook(docData, docId) {
  try {
    const payload = {
      documentId: docId,
      title: docData.title,
      sourceUrl: docData.sourceUrl,
      pdfUrl: docData.pdfUrl,
      incidentDate: docData.incidentDate,
      industry: docData.industry,
      source: 'csb',
      ppiManaged: true,
      targetVisibility: 'global',
      batchId: docData.batchId,
    };

    const response = await fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      return { success: false, error: `Webhook returned ${response.status}: ${errorText}` };
    }

    const result = await response.json().catch(() => ({}));
    return { success: true, responseId: result.id || result.executionId || null };
  } catch (error) {
    return { success: false, error: error.message || 'Webhook call failed' };
  }
}

/**
 * Process a batch: fire webhooks for all queued documents with rate limiting.
 *
 * Updates each document's status in Firestore as it progresses:
 *   queued → processing → completed | error
 *
 * @param {string} batchId
 * @param {Function} [onProgress] - Called with { processed, total, currentTitle } after each doc
 * @returns {Promise<{processed: number, succeeded: number, failed: number}>}
 */
export async function processBatch(batchId, onProgress) {
  const docs = await getBatchDocuments(batchId);
  const queuedDocs = docs.filter((d) => d.status === 'queued');

  let processed = 0;
  let succeeded = 0;
  let failed = 0;
  const total = queuedDocs.length;

  for (const docItem of queuedDocs) {
    // Mark as processing
    const docRef = doc(db, collections.documents, docItem.id);
    await updateDoc(docRef, {
      status: 'processing',
      processingStartedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    // Fire webhook
    const result = await fireWebhook(docItem, docItem.id);

    if (result.success) {
      await updateDoc(docRef, {
        status: 'completed',
        webhookResponseId: result.responseId,
        processingCompletedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      succeeded++;
    } else {
      await updateDoc(docRef, {
        status: 'error',
        error: result.error,
        updatedAt: Timestamp.now(),
      });
      failed++;
    }

    processed++;

    if (typeof onProgress === 'function') {
      onProgress({ processed, total, currentTitle: docItem.title });
    }

    // Rate limit: wait before next webhook call (unless last item)
    if (processed < total) {
      await delay(RATE_LIMIT_DELAY_MS);
    }
  }

  return { processed, succeeded, failed };
}

/**
 * Retry failed documents in a batch.
 *
 * @param {string} batchId
 * @returns {Promise<number>} Number of documents reset to queued
 */
export async function retryFailedInBatch(batchId) {
  const docs = await getBatchDocuments(batchId);
  const failedDocs = docs.filter((d) => d.status === 'error');

  for (const docItem of failedDocs) {
    const docRef = doc(db, collections.documents, docItem.id);
    await updateDoc(docRef, {
      status: 'queued',
      error: null,
      processingStartedAt: null,
      processingCompletedAt: null,
      updatedAt: Timestamp.now(),
    });
  }

  return failedDocs.length;
}
